import { useState, useCallback, useMemo, useEffect } from 'react';

// Page dimensions at 96 DPI
export const PAGE_DIMENSIONS = {
  a4: {
    portrait: { width: 794, height: 1123 },
    landscape: { width: 1123, height: 794 }
  },
  letter: {
    portrait: { width: 816, height: 1056 },
    landscape: { width: 1056, height: 816 }
  }
};

// Convert mm to pixels at 96 DPI
export const mmToPixels = (mm: number): number => Math.round((mm / 25.4) * 96);

export interface PageSettings {
  size: 'a4' | 'letter';
  orientation: 'portrait' | 'landscape';
  margins: number; // in mm
}

export interface SectionData {
  id: string;
  title: string;
  type: string;
  contentKey?: string;
  matrixIndex?: number;
  isVisible: boolean;
  orderIndex: number;
}

// A colored sub-block discovered inside a section's rendered DOM (e.g. the
// "Top Concerns" card inside an Executive Summary). Discovered generically by
// SectionRenderer so granular removal works across all report types.
export interface DiscoveredBlock {
  id: string;    // `${sectionId}::${slug}` — stable across render instances
  title: string; // heading text used as the sidebar label
}

interface UsePdfExportStateV2Props {
  initialSections: SectionData[];
}

interface UsePdfExportStateV2Return {
  // State
  sections: SectionData[];
  pageSettings: PageSettings;
  zoom: number;
  pageBreaksAfter: Set<string>;
  pageBreaksBefore: Set<string>;
  selectedSectionId: string | null;
  sectionOffsets: Map<string, number>;
  isEditMode: boolean;
  editedContent: Map<string, string>;
  removedBlocks: Set<string>;
  discoveredBlocks: Map<string, DiscoveredBlock[]>;

  // Computed
  visibleSections: SectionData[];
  pageDimensions: { width: number; height: number };
  contentDimensions: { width: number; height: number };
  marginPixels: number;

  // Actions
  toggleSection: (sectionId: string) => void;
  toggleAllSections: (visible: boolean) => void;
  reorderSection: (fromIndex: number, toIndex: number) => void;
  togglePageBreak: (sectionId: string) => void;
  togglePageBreakBefore: (sectionId: string) => void;
  setPageSettings: (settings: Partial<PageSettings>) => void;
  setZoom: (zoom: number) => void;
  resetToDefaults: () => void;
  selectSection: (sectionId: string | null) => void;
  adjustSectionOffset: (sectionId: string, offset: number) => void;
  nudgeSection: (sectionId: string, direction: 'up' | 'down', amount?: number) => void;
  setEditMode: (enabled: boolean) => void;
  updateEditedContent: (contentPath: string, value: string) => void;
  getEditedContent: (contentPath: string, originalValue: string) => string;
  toggleBlock: (blockId: string) => void;
  registerBlocks: (sectionId: string, blocks: DiscoveredBlock[]) => void;
}

const DEFAULT_PAGE_SETTINGS: PageSettings = {
  size: 'a4',
  orientation: 'portrait',
  margins: 15
};

const DEFAULT_ZOOM = 0.75;
const NUDGE_AMOUNT = 20; // pixels per nudge

export function usePdfExportStateV2({
  initialSections
}: UsePdfExportStateV2Props): UsePdfExportStateV2Return {
  // State
  const [sections, setSections] = useState<SectionData[]>(initialSections);
  const [pageSettings, setPageSettingsState] = useState<PageSettings>(DEFAULT_PAGE_SETTINGS);
  const [zoom, setZoomState] = useState<number>(DEFAULT_ZOOM);
  const [pageBreaksAfter, setPageBreaksAfter] = useState<Set<string>>(new Set());
  const [pageBreaksBefore, setPageBreaksBefore] = useState<Set<string>>(new Set());
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  const [sectionOffsets, setSectionOffsets] = useState<Map<string, number>>(new Map());
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [editedContent, setEditedContent] = useState<Map<string, string>>(new Map());
  const [removedBlocks, setRemovedBlocks] = useState<Set<string>>(new Set());
  const [discoveredBlocks, setDiscoveredBlocks] = useState<Map<string, DiscoveredBlock[]>>(new Map());

  // Update sections when initialSections changes (async data loading)
  useEffect(() => {
    if (initialSections.length > 0) {
      setSections(initialSections);
    }
  }, [initialSections]);

  // Computed: visible sections sorted by order
  const visibleSections = useMemo(() => {
    return sections
      .filter(s => s.isVisible)
      .sort((a, b) => a.orderIndex - b.orderIndex);
  }, [sections]);

  // Computed: page dimensions
  const pageDimensions = useMemo(() => {
    return PAGE_DIMENSIONS[pageSettings.size][pageSettings.orientation];
  }, [pageSettings.size, pageSettings.orientation]);

  // Computed: margin in pixels
  const marginPixels = useMemo(() => {
    return mmToPixels(pageSettings.margins);
  }, [pageSettings.margins]);

  // Computed: content area dimensions
  const contentDimensions = useMemo(() => {
    return {
      width: pageDimensions.width - (marginPixels * 2),
      height: pageDimensions.height - (marginPixels * 2)
    };
  }, [pageDimensions, marginPixels]);

  // Actions
  const toggleSection = useCallback((sectionId: string) => {
    setSections(prev => prev.map(s =>
      s.id === sectionId ? { ...s, isVisible: !s.isVisible } : s
    ));
  }, []);

  const toggleAllSections = useCallback((visible: boolean) => {
    setSections(prev => prev.map(s => ({ ...s, isVisible: visible })));
  }, []);

  const reorderSection = useCallback((fromIndex: number, toIndex: number) => {
    setSections(prev => {
      const newSections = [...prev];
      const visibleIds = newSections
        .filter(s => s.isVisible)
        .sort((a, b) => a.orderIndex - b.orderIndex)
        .map(s => s.id);

      // Reorder the visible IDs
      const [movedId] = visibleIds.splice(fromIndex, 1);
      visibleIds.splice(toIndex, 0, movedId);

      // Update orderIndex based on new positions
      return newSections.map(s => {
        const newIndex = visibleIds.indexOf(s.id);
        return newIndex >= 0 ? { ...s, orderIndex: newIndex } : s;
      });
    });
  }, []);

  const togglePageBreak = useCallback((sectionId: string) => {
    setPageBreaksAfter(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const togglePageBreakBefore = useCallback((sectionId: string) => {
    setPageBreaksBefore(prev => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  }, []);

  const setPageSettings = useCallback((settings: Partial<PageSettings>) => {
    setPageSettingsState(prev => ({ ...prev, ...settings }));
  }, []);

  const setZoom = useCallback((newZoom: number) => {
    setZoomState(Math.max(0.25, Math.min(1.5, newZoom)));
  }, []);

  const selectSection = useCallback((sectionId: string | null) => {
    setSelectedSectionId(sectionId);
  }, []);

  const adjustSectionOffset = useCallback((sectionId: string, offset: number) => {
    setSectionOffsets(prev => {
      const next = new Map(prev);
      if (offset === 0) {
        next.delete(sectionId);
      } else {
        next.set(sectionId, offset);
      }
      return next;
    });
  }, []);

  const nudgeSection = useCallback((sectionId: string, direction: 'up' | 'down', amount: number = NUDGE_AMOUNT) => {
    setSectionOffsets(prev => {
      const next = new Map(prev);
      const currentOffset = prev.get(sectionId) || 0;
      const newOffset = direction === 'down'
        ? currentOffset + amount
        : currentOffset - amount;

      // Clamp to reasonable values
      const clampedOffset = Math.max(-200, Math.min(500, newOffset));

      if (clampedOffset === 0) {
        next.delete(sectionId);
      } else {
        next.set(sectionId, clampedOffset);
      }
      return next;
    });
  }, []);

  const resetToDefaults = useCallback(() => {
    setSections(initialSections);
    setPageSettingsState(DEFAULT_PAGE_SETTINGS);
    setZoomState(DEFAULT_ZOOM);
    setPageBreaksAfter(new Set());
    setPageBreaksBefore(new Set());
    setSelectedSectionId(null);
    setSectionOffsets(new Map());
    setIsEditMode(false);
    setEditedContent(new Map());
    setRemovedBlocks(new Set());
  }, [initialSections]);

  // Edit mode actions
  const setEditMode = useCallback((enabled: boolean) => {
    setIsEditMode(enabled);
  }, []);

  const updateEditedContent = useCallback((contentPath: string, value: string) => {
    setEditedContent(prev => {
      const next = new Map(prev);
      next.set(contentPath, value);
      return next;
    });
  }, []);

  const getEditedContent = useCallback((contentPath: string, originalValue: string): string => {
    return editedContent.get(contentPath) ?? originalValue;
  }, [editedContent]);

  // Toggle removal of a colored sub-block (by stable block id).
  const toggleBlock = useCallback((blockId: string) => {
    setRemovedBlocks(prev => {
      const next = new Set(prev);
      if (next.has(blockId)) next.delete(blockId);
      else next.add(blockId);
      return next;
    });
  }, []);

  // Record the sub-blocks SectionRenderer found inside a section. Called from
  // multiple render instances (measurement + each page) with identical lists —
  // skip the state update when nothing changed to avoid render loops.
  const registerBlocks = useCallback((sectionId: string, blocks: DiscoveredBlock[]) => {
    setDiscoveredBlocks(prev => {
      const existing = prev.get(sectionId);
      const unchanged =
        existing &&
        existing.length === blocks.length &&
        existing.every((b, i) => b.id === blocks[i].id && b.title === blocks[i].title);
      if (unchanged) return prev;
      const next = new Map(prev);
      next.set(sectionId, blocks);
      return next;
    });
  }, []);

  return {
    sections,
    pageSettings,
    zoom,
    pageBreaksAfter,
    pageBreaksBefore,
    selectedSectionId,
    sectionOffsets,
    isEditMode,
    editedContent,
    removedBlocks,
    discoveredBlocks,
    visibleSections,
    pageDimensions,
    contentDimensions,
    marginPixels,
    toggleSection,
    toggleAllSections,
    reorderSection,
    togglePageBreak,
    togglePageBreakBefore,
    setPageSettings,
    setZoom,
    resetToDefaults,
    selectSection,
    adjustSectionOffset,
    nudgeSection,
    setEditMode,
    updateEditedContent,
    getEditedContent,
    toggleBlock,
    registerBlocks
  };
}

export default usePdfExportStateV2;
